import { format } from 'date-fns';
import {
  DisputeDeviationResolutionDto,
  FiltersRequestDto,
  InvoiceDisputeUpdateRequestDto,
  InvoiceSearchRequestDto,
  InvoiceSummaryDto,
  ResponseDtoInvoiceSearchResponse,
} from 'src/generated/services/TFinancialApi';
import { ITableData, ITableParams } from 'src/types/table';
import { IDisputeTableItem } from 'src/presentations/components/organisms/actionDetails/dispute/types';

export const tableParamsToInvoiceSearchRequest = (tableParams: ITableParams): InvoiceSearchRequestDto => {
  const filters = tableParams?.filters?.filters || tableParams?.filters;
  const apiParams = {
    page: {
      pageNumber: tableParams?.page || 1,
      pageSize: tableParams?.pageSize || 25,
    },
    sorting: {
      field: tableParams?.orderBy || 'INVOICE_DATE',
      sortingDirection: tableParams?.orderMethod === 'asc' ? 'ASC' : 'DESC',
    },
    searchFilters: filters,
    invoiceStatusCategory: tableParams?.filters.invoiceStatusCategory,
    invoiceStatusSet: Array.isArray(tableParams?.filters.invoiceStatusSet)
      ? tableParams?.filters.invoiceStatusSet
      : tableParams?.filters.invoiceStatusSet?.split(','),
    dateReference: tableParams?.filters.dateReference,
    currencyReference: tableParams?.filters.currencyReference,
    invoiceNumber: Array.isArray(filters.invoiceNumber) ? filters.invoiceNumber : filters.invoiceNumber?.split(','),
    // Additional filter fields for full column filtering support
    // Extract from nested filters structure
    shipmentType: (() => {
      const shipmentType = filters?.shipmentFilters?.shipmentType;
      return Array.isArray(shipmentType) ? shipmentType : shipmentType?.split(',');
    })(),
    shipmentNumber: Array.isArray(filters?.shipmentFilters?.shipmentNumber)
      ? filters?.shipmentFilters?.shipmentNumber
      : filters?.shipmentFilters?.shipmentNumber?.split(','),
    tradeLane: Array.isArray(filters?.tradeLaneFilters?.tradeLane) ? filters?.tradeLaneFilters?.tradeLane : filters?.tradeLaneFilters?.tradeLane?.split(','),
    billedEntity: Array.isArray(filters?.billedEntity) ? filters?.billedEntity : filters?.billedEntity?.split(','),
    incoterm: Array.isArray(filters?.shipmentFilters?.incoterm) ? filters?.shipmentFilters?.incoterm : filters?.shipmentFilters?.incoterm?.split(','),
    shipper: Array.isArray(filters?.shipmentFilters?.shipper) ? filters?.shipmentFilters?.shipper : filters?.shipmentFilters?.shipper?.split(','),
    consignee: Array.isArray(filters?.shipmentFilters?.consignee) ? filters?.shipmentFilters?.consignee : filters?.shipmentFilters?.consignee?.split(','),
    service: Array.isArray(filters?.shipmentFilters?.service) ? filters?.shipmentFilters?.service : filters?.shipmentFilters?.service?.split(','),
    date: Array.isArray(filters?.dateFilters?.date) ? filters?.dateFilters?.date : filters?.dateFilters?.date?.split(','),
  };

  // Override with keyword search if provided
  if (tableParams?.keyword) {
    apiParams.invoiceNumber = [tableParams?.keyword];
  }
  return apiParams as InvoiceSearchRequestDto;
};

export const invoiceSearchResponseToTableData = (response: ResponseDtoInvoiceSearchResponse): ITableData => {
  const total = response?.data?.totalRecord;

  const invoices = response?.data?.invoices?.map((item) => ({
    ...item,
    redirectId: item.id,
  }));

  const stats = {};

  const allowedCurrencies = [];

  return { items: invoices || [], total: total || 0, stats, allowedCurrencies };
};

export const tableFiltersToInvoiceDashboardFilters = (filterOptions: Record<string, any> = null): FiltersRequestDto => {
  const apiParams = {
    filters: filterOptions?.filters || filterOptions,
    invoiceStatusSet: Array.isArray(filterOptions?.invoiceStatusSet) ? filterOptions?.invoiceStatusSet : filterOptions?.invoiceStatusSet?.split(','),
    dateReference: filterOptions?.dateReference,
    currencyReference: filterOptions?.currencyReference,
    // Additional filter fields for full column filtering support
    shipmentType: Array.isArray(filterOptions?.shipmentType) ? filterOptions?.shipmentType : filterOptions?.shipmentType?.split(','),
    shipmentNumber: Array.isArray(filterOptions?.shipmentNumber) ? filterOptions?.shipmentNumber : filterOptions?.shipmentNumber?.split(','),
    tradeLane: Array.isArray(filterOptions?.tradeLane) ? filterOptions?.tradeLane : filterOptions?.tradeLane?.split(','),
    billedEntity: Array.isArray(filterOptions?.billedEntity) ? filterOptions?.billedEntity : filterOptions?.billedEntity?.split(','),
    incoterm: Array.isArray(filterOptions?.incoterm) ? filterOptions?.incoterm : filterOptions?.incoterm?.split(','),
    shipper: Array.isArray(filterOptions?.shipper) ? filterOptions?.shipper : filterOptions?.shipper?.split(','),
    consignee: Array.isArray(filterOptions?.consignee) ? filterOptions?.consignee : filterOptions?.consignee?.split(','),
    service: Array.isArray(filterOptions?.service) ? filterOptions?.service : filterOptions?.service?.split(','),
    date: Array.isArray(filterOptions?.date) ? filterOptions?.date : filterOptions?.date?.split(','),
  };

  return apiParams as FiltersRequestDto;
};

export const DisputeFormToBody = (formValue, id: string): InvoiceDisputeUpdateRequestDto => ({
  id,
  lumpSumAmount: formValue.totalAmount ? Number(Number.parseFloat(formValue.totalAmount).toFixed(2)) : 0,
  agreementResolution: formValue.agreementType === 'CANCEL_DISPUTE' ? null : formValue.agreementType,
  deviationResolutionType: formValue.totalAmount && formValue.totalAmount !== '0' ? 'LUMP_SUM' : null,
  resolutionDescription: formValue.reason,
  deviationResolutions: formValue.disputeTableItems?.map(
    (item: IDisputeTableItem): DisputeDeviationResolutionDto => ({
      deviationId: item.deviationId,
      startPeriod: item.validityPeriod ? format(item.validityPeriod?.start, 'yyyy-MM-dd') : null,
      endPeriod: item.validityPeriod ? format(item.validityPeriod?.end, 'yyyy-MM-dd') : null,
      expectedAmount: item.expectedAmount,
      periodType: item.oneOffOrRecurring,
    })
  ),
});

export const invoiceFinalResolutionMapper = (finalResolution: InvoiceSummaryDto['finalResolution'] | null): string => {
  const invoiceFinalResolutionMap: Record<InvoiceSummaryDto['finalResolution'], string> = {
    DUPLICATE_INVOICE: 'Duplicated Invoice',
    NO_RATE_CARD_FOUND: 'No Rate Card Found',
    NO_CONTRACT_REJECT: 'Invoice rejected due to No Rate Card Found',
    REJECT_AFTER_DISPUTE: 'Invoice Rejected after Dispute',
    UNKNOWN_BILLING_PARTY: 'Unknown Billed Party',
    WRONG_BILLING_PARTY: 'Wrong Billed Party',
    MISSING_SIC_CODE: 'Missing SIC Code',
  };

  return invoiceFinalResolutionMap[finalResolution] ?? 'Invoice Rejected';
};

export const checkShouldFetchDraft = (currentDeviationWorkflow: string | undefined): boolean => {
  const validDraftWorkflows = ['DISPUTE_REVIEW', 'PROCUREMENT_NO_CONTRACT_REVIEW', 'OPS_ADMIN_NO_CONTRACT_REVIEW'];
  return validDraftWorkflows.includes(currentDeviationWorkflow);
};
